import { inject, Inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { VehicleTypeModel } from '../models/VehicleTypeModel';
import { SortDirectionValuesModel } from '../models/SortDirectionValues-model';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from './Notification.service';
import { DeleteDialogComponent } from '../helpers/delete-dialog/delete-dialog.component';

@Injectable({
    providedIn: 'root'
})
export class VehicleTypeService {
    private url = `${this.API_URL}`;

    public vehicleTypes = signal<VehicleTypeModel[]>([]);
    public typeIndex = signal<number | null>(null);
    public editInputRow = signal(false);
    public newInputRow = signal<VehicleTypeModel>({} as VehicleTypeModel);
    public isLoading = signal<boolean>(false);
    public isAddingNewType = signal<boolean>(false);

    public pageNumber = signal<number>(0);
    public pageSize = signal<number>(50);
    public sortDirectionValues = signal<SortDirectionValuesModel>({ active: 'id', direction: 'asc' });

    public notificationService = inject(NotificationService);
    dialog = inject(MatDialog);

    constructor(@Inject('API_URL') private API_URL: string, private httpClient: HttpClient) {}


    triggerAllVehicleTypes() {
        return this.httpClient.get<{
            totalPages: number;
            totalElements: number;
            size: number;
            content: VehicleTypeModel[];
            number: number;
        }>(`${this.url}/vehicle-types`, {
            params: {
                sortAttribute: this.sortDirectionValues().active,
                sortDirection: this.sortDirectionValues().direction,
                pageNumber: this.pageNumber(),
                pageSize: this.pageSize()
            }
        });
    }


    getVehicleTypesTable() {
        this.isLoading.set(true);
        this.triggerAllVehicleTypes().subscribe({
            next: (response) => {
                this.vehicleTypes.set(response.content);
                this.isLoading.set(false);
            },
            error: (error) => {
                this.isLoading.set(false);
                this.notificationService.showErrorMessage('Error loading vehicle types', 'Error');
            }
        });
    }

    getVehicleTypesOnScroll() {
        this.triggerAllVehicleTypes().subscribe({
            next: (response) => {
                this.vehicleTypes.update((types) => [...types, ...response.content]);
                this.isLoading.set(false);
            },
            error: (error) => {
                this.isLoading.set(false);
                this.notificationService.showErrorMessage('Error loading more vehicle types', 'Error');
            }
        });
    }

    checkDuplicateType(value: string): boolean {
        const lowerName = value.trim().toLowerCase();
        return this.vehicleTypes().some(
            (t) => t.correctVehicleType?.trim().toLowerCase() === lowerName
        );
    }

    onScroll() {
        if (this.isLoading()) {
            console.log('Already loading, skipping...');
            return;
        }

        this.isLoading.set(true);
        this.increasePageNumber();
        this.getVehicleTypesOnScroll();
    }


    increasePageNumber() {
        this.pageNumber.update((prevNum) => prevNum + 1);
    }

    resetPageNumber() {
        this.pageNumber.set(0);
    }


    changeSortDirection(sortDirection: SortDirectionValuesModel, tableElement?: HTMLElement) {
        try {
            this.pageNumber.set(0);
            this.sortDirectionValues.set(sortDirection);
            if (tableElement) tableElement.scrollTop = 0;

            this.triggerAllVehicleTypes().subscribe({
                next: (response) => {
                    this.vehicleTypes.set([...response.content]);
                },
                error: (error) => {
                    if (error.status === 409) {
                        this.notificationService.showErrorMessage('This attribute does not exist in this table', 'Error');
                    } else {
                        this.notificationService.showErrorMessage('Something went wrong', 'Error Occurred');
                    }
                    this.resetSortDirection();
                }
            });
        } catch {
            this.notificationService.showErrorMessage('Error occurred while sorting', 'Error');
        }
    }

    resetSortDirection() {
        this.sortDirectionValues.set({ active: 'id', direction: 'asc' });
    }


    onRefreshTable() {
        this.pageNumber.set(0);
        this.pageSize.set(50);
        this.sortDirectionValues.set({ active: 'id', direction: 'asc' });
        this.vehicleTypes.set([]);
        this.editInputRow.set(false);
        this.typeIndex.set(null);
        this.isAddingNewType.set(false);
        this.resetNewInputRow();
        this.getVehicleTypesTable();
    }


    addVehicleType() {
        this.isAddingNewType.set(true);
        this.httpClient.post<VehicleTypeModel>(
            `${this.url}/vehicle-types`,
            {
                correctVehicleType: this.newInputRow().correctVehicleType,
                acceptedVehicleType: this.newInputRow().acceptedVehicleType
            },
            { observe: 'response' }
        ).subscribe({
            next: (response) => {
                if (response.status === 201) {
                    this.onRefreshTable();
                    this.notificationService.showSuccessMessage('Successfully added', 'Successfully added');
                }
                this.isAddingNewType.set(false);
                this.resetNewInputRow();
                this.editInputRow.set(false);
            },
            error: (err) => {
                this.isAddingNewType.set(false);
                this.handleError(err);
            }
        });
    }


    updateVehicleType(newType: VehicleTypeModel) {
        if (!newType.id) {
            this.notificationService.showErrorMessage('Missing ID for update', 'Error');
            return;
        }
        this.isAddingNewType.set(true);

        this.httpClient.put<VehicleTypeModel>(`${this.url}/vehicle-types/${newType.id}`,
                {
                    correctVehicleType: newType.correctVehicleType,
                    acceptedVehicleType: newType.acceptedVehicleType
                },
                { observe: 'response' }
            )
            .subscribe({
                next: (response) => {
                    if (response.status === 200 && response.body) {
                        this.vehicleTypes.update((types) =>
                            types.map((prevType) => (prevType.id === newType.id ? response.body! : prevType))
                        );
                        this.typeIndex.set(null);
                        this.notificationService.showSuccessMessage('Vehicle type updated successfully', 'Success');
                    }
                    this.isAddingNewType.set(false);
                },
                error: (err) => {
                    this.isAddingNewType.set(false);
                    this.handleError(err);
                }
            });
    }


    deleteVehicleType(type: VehicleTypeModel) {
        const dialogRef = this.dialog.open(DeleteDialogComponent, {
            width: '350px',
            data: 'Are you sure you want to delete this vehicle type?'
        });

        dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed && type.id) {
                this.isLoading.set(true);
                this.httpClient
                    .delete(`${this.url}/vehicle-types/${type.id}`, { observe: 'response' })
                    .subscribe({
                        next: (response) => {
                            if (response.status === 200 || response.status === 204) {
                                this.vehicleTypes.update((types) => types.filter((t) => t.id !== type.id));
                                this.notificationService.showSuccessMessage('Vehicle type deleted successfully', 'Deleted');
                            }
                            this.isLoading.set(false);
                        },
                        error: (err) => {
                            this.isLoading.set(false);
                            this.handleError(err);
                        }
                    });
            }
        });
    }

    deleteVehicleTypeRequest(index: number) {
        this.vehicleTypes.update((types) => types.filter((_, i) => i !== index));
        this.notificationService.showSuccessMessage('Successfully deleted', `Deleted row ${index + 1}`);
    }


    resetNewInputRow() {
        this.newInputRow.set({} as VehicleTypeModel);
    }

    openNewRow() {
        this.editInputRow.set(!this.editInputRow());
        this.resetNewInputRow();
    }

    resetComponentState() {
        this.editInputRow.set(false);
        this.typeIndex.set(null);
        this.resetNewInputRow();
        this.isAddingNewType.set(false);
    }

    private handleError(err: any) {
        if (err.status === 400) {
            const errors = err?.error?.errors;
            if (errors && typeof errors === 'object') {
                Object.entries(errors).forEach(([field, message]) => {
                    if (field === 'correctVehicleType') {
                        this.notificationService.showErrorMessage(`Correct Vehicle Type: ${message}`, 'Error');
                    } else if (field === 'acceptedVehicleType') {
                        this.notificationService.showErrorMessage(`Accepted Vehicle Type: ${message}`, 'Error');
                    }
                });
            } else {
                this.notificationService.showErrorMessage('Invalid input data', 'Validation Error');
            }
        } else if (err.status === 409) {
            const backendMessage = err?.error?.message;
            if (backendMessage) {
                this.notificationService.showErrorMessage(backendMessage, 'Error');
            } else {
                this.notificationService.showErrorMessage('Vehicle type already exists', 'Error');
            }
        } else if (err.status === 404) {
            this.notificationService.showErrorMessage('Vehicle type not found', 'Not Found');
        } else {
            this.notificationService.showErrorMessage('Unexpected error occurred', 'Error');
        }
    }
}
